import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  type AgentCard,
  type AgentConfig,
  type AgentConfigErrorCode,
  type AgentCounts,
  type AgentType,
  type BduiSchema,
} from '../models/agent-config.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface AgentSliceError {
  code: AgentConfigErrorCode;
  detail: string;
}

export type AgentListStatus = 'idle' | 'loading' | 'success' | 'error';
export type AgentSearchStatus = 'idle' | 'loading' | 'success' | 'error';
export type AgentCountsStatus = 'idle' | 'loading' | 'success' | 'error';
export type AgentCurrentStatus =
  | 'idle'
  | 'loading'
  | 'saving'
  | 'success'
  | 'error';
export type AgentSchemaStatus = 'loading' | 'success' | 'error';
export type AgentCardStatus = 'loading' | 'success' | 'error';

export interface AgentCardSlot {
  data: AgentCard | null;
  status: AgentCardStatus;
  /** ms epoch — used to invalidate cache after a TTL window. */
  fetchedAt: number;
}

export interface AgentDraftFile {
  name: string;
  size: number;
  type: string;
}

export type AgentTypesStatus = 'idle' | 'loading' | 'success' | 'error';

export interface AgentConfigState {
  list: {
    data: AgentConfig[];
    status: AgentListStatus;
    error: AgentSliceError | null;
  };
  counts: {
    data: AgentCounts;
    status: AgentCountsStatus;
  };
  search: {
    /** Last query the latest fulfilled or in-flight result corresponds to. */
    query: string;
    data: AgentConfig[];
    status: AgentSearchStatus;
  };
  current: {
    id: string | null;
    data: AgentConfig | null;
    status: AgentCurrentStatus;
    error: AgentSliceError | null;
  };
  schemaCache: Record<string, BduiSchema>;
  schemaStatus: Record<string, AgentSchemaStatus>;
  cardCache: Record<string, AgentCardSlot>;
  agentTypes: {
    allowed: AgentType[];
    catalog: AgentType[];
    status: AgentTypesStatus;
  };
  creationDraft: {
    files: AgentDraftFile[];
  };
}

const initialState: AgentConfigState = {
  list: { data: [], status: 'idle', error: null },
  counts: {
    data: { all: 0, active: 0, paused: 0, draft: 0 },
    status: 'idle',
  },
  search: { query: '', data: [], status: 'idle' },
  current: { id: null, data: null, status: 'idle', error: null },
  schemaCache: {},
  schemaStatus: {},
  cardCache: {},
  agentTypes: { allowed: [], catalog: [], status: 'idle' },
  creationDraft: { files: [] },
};

export const schemaCacheKey = (agentType: AgentType, lang: string) =>
  `${agentType}:${lang}`;

export const agentConfigSlice = createSlice({
  name: 'agentConfig',
  initialState,
  reducers: {
    setListLoading: (state) => {
      state.list.status = 'loading';
      state.list.error = null;
    },
    setListData: (state, action: PayloadAction<AgentConfig[]>) => {
      state.list.status = 'success';
      state.list.data = action.payload;
      state.list.error = null;
    },
    setListError: (state, action: PayloadAction<AgentSliceError>) => {
      state.list.status = 'error';
      state.list.error = action.payload;
    },
    upsertListItem: (state, action: PayloadAction<AgentConfig>) => {
      const idx = state.list.data.findIndex(
        (a) => a._id === action.payload._id,
      );
      if (idx >= 0) state.list.data[idx] = action.payload;
      else state.list.data.unshift(action.payload);
      const sIdx = state.search.data.findIndex(
        (a) => a._id === action.payload._id,
      );
      if (sIdx >= 0) state.search.data[sIdx] = action.payload;
    },
    removeListItem: (state, action: PayloadAction<string>) => {
      state.list.data = state.list.data.filter((a) => a._id !== action.payload);
      state.search.data = state.search.data.filter(
        (a) => a._id !== action.payload,
      );
    },
    patchListItemActive: (
      state,
      action: PayloadAction<{ id: string; active: boolean }>,
    ) => {
      const apply = (a: AgentConfig | undefined | null) => {
        if (!a) return;
        a.active = action.payload.active;
        // Activating clears the draft flag — mirrors service-layer invariant.
        if (action.payload.active) a.is_draft = false;
      };
      apply(state.list.data.find((a) => a._id === action.payload.id));
      if (state.current.data && state.current.data._id === action.payload.id) {
        apply(state.current.data);
      }
      apply(state.search.data.find((a) => a._id === action.payload.id));
    },

    patchListItemDraft: (
      state,
      action: PayloadAction<{ id: string; is_draft: boolean }>,
    ) => {
      const apply = (a: AgentConfig | undefined | null) => {
        if (!a) return;
        a.is_draft = action.payload.is_draft;
        // Service forces active=false on every draft transition (both ways).
        a.active = false;
      };
      apply(state.list.data.find((a) => a._id === action.payload.id));
      if (state.current.data && state.current.data._id === action.payload.id) {
        apply(state.current.data);
      }
      apply(state.search.data.find((a) => a._id === action.payload.id));
    },

    setCountsLoading: (state) => {
      state.counts.status = 'loading';
    },
    setCountsData: (state, action: PayloadAction<AgentCounts>) => {
      state.counts.data = action.payload;
      state.counts.status = 'success';
    },
    setCountsError: (state) => {
      state.counts.status = 'error';
    },

    setSearchLoading: (state, action: PayloadAction<string>) => {
      state.search.query = action.payload;
      state.search.status = 'loading';
    },
    setSearchData: (
      state,
      action: PayloadAction<{ query: string; data: AgentConfig[] }>,
    ) => {
      state.search.query = action.payload.query;
      state.search.data = action.payload.data;
      state.search.status = 'success';
    },
    setSearchError: (state, action: PayloadAction<string>) => {
      state.search.query = action.payload;
      state.search.status = 'error';
    },
    clearSearch: (state) => {
      state.search = initialState.search;
    },

    setCurrentLoading: (state, action: PayloadAction<string>) => {
      state.current.id = action.payload;
      state.current.status = 'loading';
      state.current.error = null;
      state.current.data = null;
    },
    setCurrentData: (state, action: PayloadAction<AgentConfig>) => {
      state.current.id = action.payload._id;
      state.current.status = 'success';
      state.current.data = action.payload;
      state.current.error = null;
    },
    setCurrentSaving: (state) => {
      state.current.status = 'saving';
      state.current.error = null;
    },
    setCurrentError: (state, action: PayloadAction<AgentSliceError>) => {
      state.current.status = 'error';
      state.current.error = action.payload;
    },
    clearCurrent: (state) => {
      state.current = initialState.current;
    },

    setSchemaLoading: (state, action: PayloadAction<string>) => {
      state.schemaStatus[action.payload] = 'loading';
    },
    setSchemaData: (
      state,
      action: PayloadAction<{ key: string; schema: BduiSchema }>,
    ) => {
      state.schemaCache[action.payload.key] = action.payload.schema;
      state.schemaStatus[action.payload.key] = 'success';
    },
    setSchemaError: (state, action: PayloadAction<string>) => {
      state.schemaStatus[action.payload] = 'error';
    },

    setCardLoading: (state, action: PayloadAction<string>) => {
      const existing = state.cardCache[action.payload];
      state.cardCache[action.payload] = {
        data: existing?.data ?? null,
        status: 'loading',
        fetchedAt: existing?.fetchedAt ?? 0,
      };
    },
    setCardData: (
      state,
      action: PayloadAction<{ id: string; card: AgentCard }>,
    ) => {
      state.cardCache[action.payload.id] = {
        data: action.payload.card,
        status: 'success',
        fetchedAt: Date.now(),
      };
    },
    setCardError: (state, action: PayloadAction<string>) => {
      const existing = state.cardCache[action.payload];
      state.cardCache[action.payload] = {
        data: existing?.data ?? null,
        status: 'error',
        fetchedAt: existing?.fetchedAt ?? 0,
      };
    },

    setDraftFiles: (state, action: PayloadAction<AgentDraftFile[]>) => {
      state.creationDraft.files = action.payload;
    },
    clearDraftFiles: (state) => {
      state.creationDraft.files = [];
    },

    setAgentTypesLoading: (state) => {
      state.agentTypes.status = 'loading';
    },
    setAgentTypesData: (
      state,
      action: PayloadAction<{ allowed: AgentType[]; catalog: AgentType[] }>,
    ) => {
      state.agentTypes.allowed = action.payload.allowed;
      state.agentTypes.catalog = action.payload.catalog;
      state.agentTypes.status = 'success';
    },
    setAgentTypesError: (state) => {
      state.agentTypes.status = 'error';
    },
  },
});

export const {
  setListLoading,
  setListData,
  setListError,
  upsertListItem,
  removeListItem,
  patchListItemActive,
  patchListItemDraft,
  setCountsLoading,
  setCountsData,
  setCountsError,
  setSearchLoading,
  setSearchData,
  setSearchError,
  clearSearch,
  setCurrentLoading,
  setCurrentData,
  setCurrentSaving,
  setCurrentError,
  clearCurrent,
  setSchemaLoading,
  setSchemaData,
  setSchemaError,
  setCardLoading,
  setCardData,
  setCardError,
  setDraftFiles,
  clearDraftFiles,
  setAgentTypesLoading,
  setAgentTypesData,
  setAgentTypesError,
} = agentConfigSlice.actions;

// Falls back to `initialState` so selectors stay safe when the slice is mounted
// lazily (combineSlices) or a stale persisted snapshot lacks it.
const selectSlice = (state: RootState): AgentConfigState =>
  state.agentConfig ?? initialState;
export const selectAgentConfigState = selectSlice;

export const selectAgentList = (state: RootState) =>
  selectSlice(state).list.data;
export const selectAgentListStatus = (state: RootState) =>
  selectSlice(state).list.status;
export const selectAgentListError = (state: RootState) =>
  selectSlice(state).list.error;

export const selectAgentCounts = (state: RootState) =>
  selectSlice(state).counts.data;
export const selectAgentCountsStatus = (state: RootState) =>
  selectSlice(state).counts.status;

export const selectAgentSearch = (state: RootState) =>
  selectSlice(state).search;
export const selectAgentSearchData = (state: RootState) =>
  selectSlice(state).search.data;
export const selectAgentSearchStatus = (state: RootState) =>
  selectSlice(state).search.status;
export const selectAgentSearchQuery = (state: RootState) =>
  selectSlice(state).search.query;

export const selectCurrentAgent = (state: RootState) =>
  selectSlice(state).current.data;
export const selectCurrentAgentStatus = (state: RootState) =>
  selectSlice(state).current.status;
export const selectCurrentAgentError = (state: RootState) =>
  selectSlice(state).current.error;

export const makeSelectSchema = (key: string) =>
  createSelector([selectSlice], (slice) => slice.schemaCache[key]);

export const makeSelectSchemaStatus = (key: string) =>
  createSelector(
    [selectSlice],
    (slice) => slice.schemaStatus[key] ?? 'loading',
  );

const EMPTY_CARD_SLOT: AgentCardSlot = {
  data: null,
  status: 'loading',
  fetchedAt: 0,
};

export const makeSelectCardSlot = (id: string) =>
  createSelector(
    [selectSlice],
    (slice) => slice.cardCache[id] ?? EMPTY_CARD_SLOT,
  );

export const selectDraftFiles = (state: RootState) =>
  selectSlice(state).creationDraft.files;

const EMPTY_TYPES: AgentType[] = [];

export const selectAllowedAgentTypes = (state: RootState): AgentType[] =>
  selectSlice(state).agentTypes?.allowed ?? EMPTY_TYPES;

export const selectAgentTypesCatalog = (state: RootState): AgentType[] =>
  selectSlice(state).agentTypes?.catalog ?? EMPTY_TYPES;

export const selectAgentTypesStatus = (state: RootState): AgentTypesStatus =>
  selectSlice(state).agentTypes?.status ?? 'idle';

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof agentConfigSlice> {}
}
